import {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  useParams,
} from 'react-router';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  Video,
  coreActions,
  coreSelectors,
  VideosRequestOrder,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';

import {
  Spinner,
  NotFound,
  ManageVideos,
  ContentWrapper,
  NavigationTabs,
  ManageVideoUpload,
} from 'web-components';

import {
  Flex,
  TextBreadcrumbs,
} from 'web-styled';

const resourceType = 'videos';
const listName = 'searchResults';

function isVideosRequestOrder(value?: string): value is VideosRequestOrder {
  if (!value) return false;
  return ['recent', 'oldest'].includes(value);
}

const VideosPage = () => {
  const params = useParams();
  const page = params.page || '1';
  const order: VideosRequestOrder = isVideosRequestOrder(params.order) ? params.order : 'recent';

  const dispatch = useDispatch();
  const mapState = useCallback(state => ({
    videos: coreSelectors.getVideos(order)(state),
    readStatus: getStatus(
      state,
      [
        'videos.requests.fetchVideos.status',
        // 'videos.requests.uploadVideos.status',
      ],
      true,
    ),
    pageMeta: coreSelectors.getRequestProperties(
      resourceType,
      'fetchVideos',
    )(state).meta,
  }), [order, page]);
  const {
    videos,
    pageMeta,
    readStatus,
  } = useSelector(mapState, shallowEqual);

  const [query, setQuery] = useState('');

  const handleSearch = () => {
    dispatch(
      coreActions.requestGetVideos({
        q: query,
        order,
        list: listName,
      }),
    );
  };

  const handleUpload = () => {
    dispatch(
      uiActions.setUiState({
        uploadVideoModal: {
          isOpen: true,
        },
      }),
    );
  };

  const handleRemove = (video: Video) => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id: video.id,
      resourceType,
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete Video?',
          bodyText: `Are you sure you want to delete <b>${video.name}</b>?`,
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  };

  const handleChangeName = (videoId: number, name: string) => {
    dispatch(
      coreActions.updateResources({
        resources: [{
          id: videoId,
          name,
        }],
        resourceType,
      }),
    );

    dispatch(
      coreActions.requestUpdateEntity({
        id: videoId,
        payload: {
          name,
        },
        resourceType,
      }),
    );
  };

  useEffect(() => {
    dispatch(
      coreActions.requestGetVideos({
        order,
        page: page ? Number(page) : 1,
        list: listName,
      }),
    );
  }, [order, page]);

  const tabs = useMemo(() => ['recent', 'oldest'].map(
    name => ({
      name,
      to: `/videos/${name}${params.page ? (`/${params.page}`) : ''}`,
    }),
  ), [params.page])

  return (
    <ContentWrapper
      header={(
        <Flex
          fillSpace
          column
          padding="0 0 15px 0"
          spacing={10}
        >
          <Flex
            width="100%"
            height="56px"
            align="center"
            justify="space-between"
          >
            <TextBreadcrumbs>
              My Video Library
            </TextBreadcrumbs>
            <NavigationTabs
              tabs={tabs}
            />
          </Flex>
          <ManageVideoUpload
            query={query}
            onSetQuery={setQuery}
            onSearch={handleSearch}
            onUpload={handleUpload}
          />
        </Flex>
      )}
    >
      <Flex
        column
        fillSpace
        width="100%"
        padding="20px 20px"
        spacing={30}
      >

        {readStatus.succeeded && (
          <ManageVideos
            videos={videos}
            pageMeta={pageMeta}
            order={order}
            readOnly={false}
            onRemove={handleRemove}
            onChangeName={handleChangeName}
          />
        )}

        {readStatus.pending && (
          <Spinner message="Loading Videos..." />
        )}

        {readStatus.failed && (
          <NotFound title="Error..." />
        )}
      </Flex>
    </ContentWrapper>
  );
};

export default VideosPage;
